import type { ComponentResponse } from '@store/generated/container';
import { enhancedApi } from '@store/generated/container';

const SERVER_UI_TAG_TYPES = ['ServerUi', 'ServerUiComponent'] as const;

export const serverUiApi = enhancedApi
  .enhanceEndpoints({
    addTagTypes: SERVER_UI_TAG_TYPES,
    endpoints: {
      getV2ProviderContainersByContainerKey: {
        providesTags: (result, _error, { containerKey, entityId }) =>
          result
            ? [{ type: 'ServerUi' as const, id: `${containerKey}:${entityId ?? 'global'}` }]
            : [],
      },
      getV2ProviderContainersByContainerKeyComponentsAndTypeKey: {
        providesTags: (result, _error, { containerKey, typeKey, entityId }) =>
          result
            ? [
                {
                  type: 'ServerUiComponent' as const,
                  id: `${containerKey}:${typeKey}:${entityId ?? 'global'}`,
                },
              ]
            : [],
      },
      // NOTE: No invalidatesTags on mutations. Invalidation is handled manually
      // in the click handlers via api.util.invalidateTags() to avoid double-refetch
      // caused by dynamicMiddleware re-entering RTK Query's middleware chain.
      postV2ProviderContainersByContainerKeyComponentsAndTypeKeyActionsActionKey: {},
      postV2ProviderContainersByContainerKeyComponentsAndTypeKeyDismiss: {},
    },
  })
  .injectEndpoints({
    endpoints: build => ({
      /**
       * Fetch a lazy SDUI component by the server-provided endpoint URL, verbatim.
       *
       * The keyed component query above addresses components by
       * (containerKey, typeKey) over the fixed /containers/.../components/... route
       * — which assumes one instance per type_key. Feature dashboards that own a
       * bespoke route (semantic-layer's /semantic/analytics/components/{slug},
       * jotbill's /billing/analytics/components/{type_key}) or emit many tiles of
       * one shared type_key can't be addressed that way. This honors the `endpoint`
       * the server already put on the lazy descriptor: the server says where to
       * fetch, the FE fetches it. Generic + reusable for any bespoke surface.
       */
      getServerUiComponentByEndpoint: build.query<ComponentResponse, string>({
        query: endpoint => endpoint,
        providesTags: (result, _error, endpoint) =>
          result ? [{ type: 'ServerUiComponent' as const, id: endpoint }] : [],
      }),
    }),
  });

export const {
  useGetV2ProviderContainersByContainerKeyQuery: useGetServerUiQuery,
  useGetV2ProviderContainersByContainerKeyComponentsAndTypeKeyQuery: useGetServerUiComponentQuery,
  useGetServerUiComponentByEndpointQuery,
  usePostV2ProviderContainersByContainerKeyComponentsAndTypeKeyActionsActionKeyMutation:
    useDispatchTileActionMutation,
  usePostV2ProviderContainersByContainerKeyComponentsAndTypeKeyDismissMutation:
    useDismissComponentMutation,
} = serverUiApi;
